import { useEffect, useRef, useState } from 'react';

import { logEvent } from '~/config/firebase.config';
import { QuizzesAPI } from '~/services/db/quizzes';
import { QuizGenerationError, QuizStudioAPI, toQuizLang } from '~/services/quizStudio/QuizStudioAPI';

import { StudioQuiz, newQuizId } from './quizTypes';

export const AI_COUNTS = [10, 15, 20] as const;
export const AI_DIFFICULTIES = ['facile', 'moyen', 'difficile', 'mix'] as const;
export type AiCount = (typeof AI_COUNTS)[number];
export type AiDifficulty = (typeof AI_DIFFICULTIES)[number];

export type AiRequest = { prompt: string; count: AiCount; difficulty: AiDifficulty; source?: string };
export const AI_SOURCE_MAX = 20000;
export const DEFAULT_AI_REQUEST: Omit<AiRequest, 'prompt'> = { count: 15, difficulty: 'mix' };

export type AiErrorCode = 'AI_NOT_CONFIGURED' | 'AUTH' | 'NO_SITE' | 'GENERATION_FAILED' | 'UNKNOWN_TOPIC' | 'SAVE_FAILED';

/** Avancée réelle : lecture de la demande → questions écrites (done/total) → vérification → enregistrement. */
export type AiProgress = { phase: 'reading' | 'writing' | 'saving'; done: number; total: number };

type State =
    | { status: 'idle' }
    | { status: 'writing'; request: AiRequest; progress: AiProgress }
    | { status: 'ready'; request: AiRequest; quiz: StudioQuiz }
    // `quiz` présent = généré mais pas enregistré : « Réessayer » ne refait que la sauvegarde.
    | { status: 'error'; request: AiRequest; code: AiErrorCode; quiz?: StudioQuiz; detail?: string };

/**
 * Création express par IA, décision Victorien du 23/09 : « jouer direct ».
 * Une phrase → le quiz est généré PUIS enregistré dans « Mes quiz » tout de
 * suite ; la relecture devient facultative (éditeur ouvert à la demande).
 */
export function useQuizGeneration(clientID: string | undefined, lang: string | undefined) {
    const [state, setState] = useState<State>({ status: 'idle' });
    // Chaque génération porte un numéro : « Annuler » ou une nouvelle demande
    // rendent la réponse en vol périmée (elle arrive ~30 s plus tard).
    const runRef = useRef(0);
    // Génération en cours : « Annuler » / nouvelle demande / démontage la coupent
    // pour de bon (sinon facturée jusqu'au bout côté serveur).
    const abortRef = useRef<AbortController | null>(null);
    const stop = () => {
        abortRef.current?.abort();
        abortRef.current = null;
    };
    useEffect(() => stop, []);

    const generate = async (request: AiRequest) => {
        const prompt = request.prompt.trim();
        if (!clientID || !prompt) return;
        const run = ++runRef.current;
        stop();
        const controller = new AbortController();
        abortRef.current = controller;
        const req = { ...request, prompt };
        setState({ status: 'writing', request: req, progress: { phase: 'reading', done: 0, total: req.count } });
        logEvent('studio_generate_quiz', clientID, { clientID, count: req.count, difficulty: req.difficulty });

        let quiz: StudioQuiz;
        try {
            const generated = await QuizStudioAPI.generateQuiz(
                {
                    theme: prompt,
                    count: req.count,
                    difficulty: req.difficulty,
                    lang: toQuizLang(lang),
                    source: req.source,
                    clientID,
                },
                (done, total) => {
                    if (run === runRef.current)
                        setState({ status: 'writing', request: req, progress: { phase: 'writing', done, total } });
                },
                controller.signal
            );
            quiz = {
                id: newQuizId(),
                title: generated.title,
                description: generated.description,
                questions: generated.questions.map((q) => ({ ...q, duration: q.duration ?? 20 })),
                scoringType: 'speed',
                lossEnabled: false,
                showOnHomepage: true,
            };
        } catch (e) {
            if (run !== runRef.current) return;
            const code = e instanceof QuizGenerationError ? e.code : 'GENERATION_FAILED';
            setState({ status: 'error', request: req, code, detail: e instanceof QuizGenerationError ? e.detail : undefined });
            return;
        }
        if (run !== runRef.current) return;
        await save(run, req, quiz);
    };

    const save = async (run: number, req: AiRequest, quiz: StudioQuiz) => {
        if (!clientID) return;
        const total = quiz.questions.length;
        setState({ status: 'writing', request: req, progress: { phase: 'saving', done: total, total } });
        try {
            await QuizzesAPI.saveQuiz(clientID, 'unquiz', quiz.id, {
                title: quiz.title,
                description: quiz.description || null,
                questions: quiz.questions,
                scoringType: quiz.scoringType,
                lossEnabled: quiz.lossEnabled,
                showOnHomepage: true,
                aiPrompt: req.prompt,
            });
        } catch {
            if (run === runRef.current) setState({ status: 'error', request: req, code: 'SAVE_FAILED', quiz });
            return;
        }
        if (run !== runRef.current) return;
        logEvent('studio_save_quiz', clientID, { clientID, quizId: quiz.id, questions: quiz.questions.length, fromAI: true });
        setState({ status: 'ready', request: req, quiz });
    };

    /** Après un échec : ne refait que la sauvegarde si le quiz est déjà écrit (même id → pas de doublon). */
    const retry = () => {
        if (state.status !== 'error') return;
        if (state.quiz) save(++runRef.current, state.request, state.quiz);
        else generate(state.request);
    };

    const reset = () => {
        runRef.current += 1;
        stop();
        setState({ status: 'idle' });
    };

    return { state, generate, retry, reset };
}
